import { Location } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    OnInit,
    signal,
    untracked,
    WritableSignal,
} from '@angular/core';
import { FormControl, FormControlStatus, FormGroup, FormsModule, Validators } from '@angular/forms';
import { firstValueFrom, Subscription } from 'rxjs';

import { LicenseListBloc } from '@connect-services/client/blocs/license-list.bloc';
import { PeopleBloc } from '@connect-services/client/blocs/people.bloc';
import { PeopleListBloc } from '@connect-services/client/blocs/people-list.bloc';
import { ProjectListBloc } from '@connect-services/client/blocs/project-list.bloc';
import { RoleListBloc } from '@connect-services/client/blocs/role-list.bloc';
import { LicensesSelectComponent } from '@connect-services/client/components/management/licenses-select/licenses-select.component';
import { FormControlStatusEnum } from '@connect-services/client/models/common.model';
import { RouteCompletePathEnum } from '@connect-services/client/models/routes.model';
import {
    AbstractUserFormComponent,
    UserFormMethodsInterface,
    UserFormType,
} from '@connect-services/client/pages/management/organization/user-edit/abstract-user-form.component';
import { DetailPanelService, PanelAction } from '@connect-services/client/services/detail-panel.service';
import { HasPermissionsService } from '@connect-services/client/services/has-permissions.service';
import { OrganizationAccessPermissionsService } from '@connect-services/client/services/organization-access-permissions.service';
import { ProjectSettingsPermissionsService } from '@connect-services/client/services/project-settings-permissions.service';
import { RoleService } from '@connect-services/client/services/role.service';
import { ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { CheckboxState } from '@connect-services/src/lib/components/checkbox/checkbox.component';
import { InputNumberComponent } from '@connect-services/src/lib/components/input/input-number/input-number.component';
import { InputTextComponent } from '@connect-services/src/lib/components/input/text/input-text/input-text.component';
import { SelectComponent, SelectOption } from '@connect-services/src/lib/components/select/select.component';
import { License, PermissionAliasEnum, Person, Project, Role, UpdatePersonDto } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { FormService } from '@shared/client/services/form.service';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormsModule,
        InputNumberComponent,
        InputTextComponent,
        LicensesSelectComponent,
        RessourcesModule,
        SelectComponent,
    ],
    selector: 'app-user-edit',
    templateUrl: './user-edit.component.html',
})
export class UserEditComponent extends AbstractUserFormComponent implements OnInit, UserFormMethodsInterface {
    public readonly isSelfEditing = computed<boolean>(() => this.securityService.meSignal()?.id === this.person()?.id);
    public readonly person: WritableSignal<Person | null> = signal(null);
    public readonly projects: WritableSignal<SelectOption<Project>[]> = signal([]);
    public readonly projectsSelected: WritableSignal<SelectOption<Project>[]> = signal([]);
    public readonly roles: WritableSignal<SelectOption<Role>[]> = signal([]);

    protected readonly canEditPerson = computed(() => this.organizationAccessPermissionsService.canManageUser());
    protected readonly canEditUserProjects = computed(() => this.projectSettingsPermissionsService.canEditProject());
    protected formGroupSubscription?: Subscription;
    protected readonly hasPermissionsService = inject(HasPermissionsService);
    protected readonly PermissionAliasEnum = PermissionAliasEnum;
    protected readonly roleService = inject(RoleService);
    private readonly destroyRef: DestroyRef = inject(DestroyRef);
    private readonly detailPanelService = inject(DetailPanelService);
    private readonly hasMissingConnectServicesRole = computed<boolean>(() => {
        return !!this.connectServicesLicense() && !this.formGroup()?.controls.role.value;
    });
    private readonly licenseListBloc = inject(LicenseListBloc);
    private readonly location = inject(Location);
    private readonly organizationAccessPermissionsService = inject(OrganizationAccessPermissionsService);
    private readonly peopleBloc = inject(PeopleBloc);
    private readonly peopleListBloc = inject(PeopleListBloc);
    private readonly projectListBloc = inject(ProjectListBloc);
    private readonly projectSettingsPermissionsService = inject(ProjectSettingsPermissionsService);
    private readonly roleListBloc = inject(RoleListBloc);
    private readonly snackbarService = inject(SnackBarService);

    public constructor() {
        super();

        effect(() => {
            if (this.connectServicesLicense()?.streetViewQuota !== null && this.hasRoleStreetViewPermission()) {
                this.formGroup()!.controls.streetViewQuota.setValue(this.connectServicesLicense()!.streetViewQuota);

                return;
            }
            this.formGroup()?.controls.streetViewQuota.setValue(null);
        });

        this.destroyRef.onDestroy(() => {
            if (this.formGroupSubscription) {
                this.formGroupSubscription.unsubscribe();
            }
            this.detailPanelService.clear();
        });

        this.detailPanelService.title.set(this.translocoService.translate('modifyUser'));
        this.detailPanelService.actions.set(this.getPanelActions());
        this.detailPanelService.navigateTo.set(RouteCompletePathEnum.USERS_ROLES);
    }

    public initForm(): FormGroup<UserFormType> {
        return new FormGroup<UserFormType>({
            firstName: new FormControl<string>(this.person()!.givenName, {
                nonNullable: true,
                validators: [
                    Validators.maxLength(FormService.VARCHAR_COMMON_LENGTH),
                    Validators.minLength(FormService.VARCHAR_MIN_LENGTH),
                    Validators.required,
                ],
            }),
            licenses: new FormControl<License[]>([], {
                nonNullable: true,
            }),
            mail: new FormControl<string>(
                { disabled: true, value: this.person()!.email },
                {
                    asyncValidators: [this.checkMailExistsValidator([this.person()!.email])],
                    nonNullable: true,
                    validators: [
                        Validators.email,
                        Validators.minLength(FormService.VARCHAR_MIN_LENGTH),
                        Validators.required,
                    ],
                },
            ),
            name: new FormControl<string>(this.person()!.familyName, {
                nonNullable: true,
                validators: [
                    Validators.maxLength(FormService.VARCHAR_COMMON_LENGTH),
                    Validators.minLength(FormService.VARCHAR_MIN_LENGTH),
                    Validators.required,
                ],
            }),
            projects: new FormControl<Project[]>([], {
                nonNullable: true,
            }),
            role: new FormControl<Role | null>(null, {
                nonNullable: !!this.connectServicesLicense(),
            }),
            streetViewQuota: new FormControl<number | null>(this.connectServicesLicense()?.streetViewQuota ?? null, {
                nonNullable: false,
                validators: [Validators.max(this.allocatableQuota() ?? 0), Validators.pattern(/^\d+$/)],
            }),
        });
    }

    public async ngOnInit(): Promise<void> {
        this.person.set(this.peopleBloc.value());

        if (!this.person() || !this.securityService.meSignal()) {
            return;
        }

        this.allocatableQuota.set(this.securityService.meSignal()!.organization.streetViewQuota);

        this.formGroup.set(this.initForm());

        await this.initLicence();
        await this.initRole();
        await this.initProject();

        this.detailPanelService.actions.set(this.getPanelActions(this.formGroup()!.status));

        this.formGroupSubscription = this.formGroup()?.statusChanges.subscribe((status: FormControlStatus) => {
            this.detailPanelService.actions.set(this.getPanelActions(status));
        });
    }

    public async submitForm(): Promise<void> {
        if (this.formGroup()!.invalid) {
            return;
        }

        if (this.canEditUserProjects()) {
            await firstValueFrom(
                this.peopleService.personControllerUpdateProjectAssignation({
                    id: this.person()!.id,
                    requestBody: this.formGroup()?.controls.projects.value?.map((project: Project) => project.id) ?? [],
                }),
            );
        }

        if (this.canEditPerson()) {
            const personDto: UpdatePersonDto = this.buildBaseUpdatePersonDto();

            this.addRoleIfChanged(personDto);

            await firstValueFrom(
                this.peopleService.personControllerPatch({
                    id: this.person()!.id,
                    updatePersonDto: {
                        ...personDto,
                    },
                }),
            );
        }

        if (this.isSelfEditing()) {
            await this.securityService.updateMe();
        }

        await this.peopleListBloc.load({}, { refresh: true, useLastLoadParameters: true });
        await this.peopleBloc.load({ id: this.person()!.id }, { refresh: true, useLastLoadParameters: true });
        await this.roleListBloc.load({}, { refresh: true, useLastLoadParameters: true });
        await this.projectListBloc.load(
            { orderBy: 'name', person: this.securityService.meSignal()?.id },
            { refresh: true },
        );
        this.snackbarService.openSuccessSnackBar('modificationsSaveSuccess');
    }

    private addRoleIfChanged(personDto: UpdatePersonDto): void {
        const prevRoleId: string | null = this.person()?.role ?? null;
        const nextRoleId: string | null = this.formGroup()!.controls.role.value?.id ?? null;

        if (prevRoleId !== nextRoleId) {
            personDto.role = nextRoleId;
        }
    }

    private buildBaseUpdatePersonDto(): UpdatePersonDto {
        return {
            email: this.formGroup()?.controls.mail.value,
            familyName: this.formGroup()?.controls.name.value,
            givenName: this.formGroup()?.controls.firstName.value,
            licenses: this.formGroup()?.controls.licenses.value.map((license: License) => license.id),
            streetViewRequestsLimit: this.formGroup()?.controls.streetViewQuota.value ?? undefined,
        };
    }

    private getPanelActions(status?: FormControlStatus): PanelAction[] {
        const isActive = status ? status === FormControlStatusEnum.VALID : (this.formGroup()?.valid ?? false);
        const missingConnectServicesRole = this.hasMissingConnectServicesRole();

        return [
            new PanelAction({
                isActive: true,
                label: this.translocoService.translate('cancel'),
                onClick: (): void => this.location.back(),
                type: ButtonType.SECONDARY,
            }),
            new PanelAction({
                disabledReason: missingConnectServicesRole
                    ? [this.translocoService.translate('missingConnectServicesRoleForSubmitTooltip')]
                    : undefined,
                icon: ['fas', 'check'],
                isActive: isActive,
                label: this.translocoService.translate('submit'),
                onClick: async (): Promise<void> => {
                    await this.submitForm();
                    this.location.back();
                },
            }),
        ];
    }

    private async initLicence(): Promise<void> {
        await this.licenseListBloc.load(
            {
                assignedOnly: true,
                person: this.person()!.id,
            },
            { refresh: true },
        );

        if (this.licenseListBloc.value()) {
            this.licensesSelected.set(untracked(() => this.licenseListBloc.value()!.items));
            this.onLicensesChange(this.licensesSelected());
        }
    }

    private async initProject(): Promise<void> {
        const selectedProjects: Project[] = await this.fetchProjects({
            person: this.person()!.id,
        });

        this.formGroup()!.controls.projects.setValue(selectedProjects);

        this.projectsSelected.set(
            selectedProjects.map((project: Project) => ({
                label: project.name,
                state: CheckboxState.CHECKED,
                value: project,
            })),
        );

        const projectsGetResult = await this.fetchProjects();

        this.projects.set(
            projectsGetResult.map((project: Project) => ({
                label: project.name,
                state: CheckboxState.UNCHECKED,
                value: project,
            })),
        );
    }

    private async initRole(): Promise<void> {
        if (!this.roleListBloc.value()) {
            return;
        }
        const roles = await this.fetchRoles();

        const roleOptions: SelectOption<Role>[] = [];
        for (const role of roles) {
            const remainingRoleCount: number | null = (await this.roleService.remainingAssignmentRolesCount(role)) ?? 0;

            const roleOption: SelectOption<Role> = {
                disabledReason:
                    !remainingRoleCount && role.maxNumberOfAssignments
                        ? this.translocoService.translate('unableToAssignRole')
                        : undefined,
                label: role.name,
                value: role,
            };

            if (role.id === this.person()?.role) {
                this.rolesSelected.update(() => [{ ...roleOption, disabledReason: undefined }]);
                this.formGroup()?.controls.role.setValue(role);
            }

            roleOptions.push(roleOption);
        }
        this.roles.set(roleOptions);
    }
}
